import { memo, ReactElement } from "react";
import { useSelector } from "react-redux";

import { useGetFormVisibility } from "../../hooks";
import { RootState } from "../../store";
import { FormHeading } from "../FormHeading";
import { Grid } from "../Grid";
import { Redirect } from "../Redirect";
import { Section } from "../Section";
import {
  FormSchema,
  ComplexFormSchemaComponents,
  SimpleFormSchemaComponents,
} from "../types";

type GeneratorProps = {
  readonly form:
    | FormSchema<SimpleFormSchemaComponents | ComplexFormSchemaComponents>
    | Array<
        FormSchema<SimpleFormSchemaComponents | ComplexFormSchemaComponents>
      >;
  readonly render: (
    arg: JSX.Element | Array<JSX.Element>
  ) => ReactElement<JSX.Element>;
};

type GenerateSingleFormProps = {
  readonly form: FormSchema<
    SimpleFormSchemaComponents | ComplexFormSchemaComponents
  >;
  readonly isLastForm: boolean;
};

const GenerateSingleForm = memo<GenerateSingleFormProps>(
  ({ form, isLastForm }) => {
    const {
      content,
      formId,
      isVisible: isVisibleSelector,
      redirect,
      selector,
    } = form;
    const formData = useSelector((state: RootState) => {
      if (!selector) {
        return {};
      }

      return selector(state);
    });
    const isVisible = useGetFormVisibility(isVisibleSelector);

    if (!isVisible) {
      return null;
    }

    return (
      <>
        {redirect && <Redirect url={redirect} />}
        {content.map(({ title, sections }, index) => {
          const bottomSpacing =
            isLastForm && ++index === content.length
              ? { xs: 0, md: 0 }
              : { xs: 10, md: 14 };

          return (
            <Grid
              key={["container", title].join("-")}
              spacingBottom={bottomSpacing}
            >
              <FormHeading
                key={["title", title].join("-")}
                component="h2"
                spacingBottom={2}
                title={title}
                variant="h3"
              />
              {sections.map((sectionProps) => (
                <Section
                  {...sectionProps}
                  key={["section", sectionProps.id].join("-")}
                  formId={formId}
                  formData={formData}
                />
              ))}
            </Grid>
          );
        })}
      </>
    );
  }
);

export const Generator = memo<GeneratorProps>(({ form, render }) => {
  if (!Array.isArray(form)) {
    return render(<GenerateSingleForm isLastForm form={form} />);
  }

  return render(
    form.map((singleForm, index) => (
      <GenerateSingleForm
        key={singleForm.id || singleForm.formId}
        isLastForm={++index === form.length}
        form={singleForm}
      />
    ))
  );
});
